<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractStatusCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const canTransitionTo = (...args) => {
    const nextStatus = args[0];
    const md = args[1];
    if (md.next_statuses.length == 0) {
      return false;
    }
    return md.next_statuses.indexOf(nextStatus) > -1;
  };

  const states = ref([
    {
      value: 'approved',
      text: $fn.value.pgettext('SetContractStatus', 'Approve...'),
      variant: 'light',
    },
    {
      value: 'signed',
      text: $fn.value.pgettext('SetContractStatus', 'Sign...'),
      variant: 'light',
    },
    {
      value: 'rescinded',
      text: $fn.value.pgettext('SetContractStatus', 'Rescind...'),
      variant: 'light',
    },
    {
      value: 'active',
      text: $fn.value.pgettext('SetContractStatus', 'Activate...'),
      variant: 'light',
    },
    {
      value: 'terminated',
      text: $fn.value.pgettext('SetContractStatus', 'Terminate...'),
      variant: 'light',
    },
    {
      value: 'ended',
      text: $fn.value.pgettext('SetContractStatus', 'End...'),
      variant: 'light',
    },
    {
      value: 'archived',
      text: $fn.value.pgettext('SetContractStatus', 'Archive...'),
      variant: 'light',
    },
  ]);
  const ux = ref([]);

  const $data = computed(() => ({
    states: states.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'states' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { states, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );

  const act_8169383091859921_emit_tnn6buo8c_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_8169383091859921_close_1b33h1f9o_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_8169383091859921_showModal_9pmtpyvo7_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractStatusTransition',
      props: {
        contract: props.contract,
        status: $event.scope.state.value,
      },
    };
  };
  const act_2857579216270778_emit_rcftwbukr_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2857579216270778_close_durgugdll_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_2857579216270778_showModal_734dyz8qe_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractStatusTransition',
      props: {
        contract: props.contract,
        status: 'unarchive',
      },
    };
  };

  const act_7315092382398562_reloadComponentData_g9e72cmod =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_b3xojrblp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_g9e72cmod,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_b3xojrblp],
    event: 'reload',
  });
  const act_8169383091859921_emit_tnn6buo8c = new actionTypes.EmitAction({
    actionArgs: act_8169383091859921_emit_tnn6buo8c_getActionArgs,
  });
  const inst_evh_8169383091859921_close_1b33h1f9o =
    new actionTypes.EventHandlerImpl({
      action: act_8169383091859921_emit_tnn6buo8c,
      event: 'close',
      when: inst_evh_8169383091859921_close_1b33h1f9o_getWhen,
      displayName: 'emit',
    });
  const act_8169383091859921_showModal_9pmtpyvo7 =
    new actionTypes.ShowModalAction({
      actionArgs: act_8169383091859921_showModal_9pmtpyvo7_getActionArgs,
      events: [inst_evh_8169383091859921_close_1b33h1f9o],
    });
  const inst_evh_8169383091859921_clickStopPrevent_gqo7lnpl5 =
    new actionTypes.EventHandlerImpl({
      action: act_8169383091859921_showModal_9pmtpyvo7,
      event: 'click.stop.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8169383091859921_clickStopPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8169383091859921_clickStopPrevent_gqo7lnpl5],
      event: 'click.stop.prevent',
    });
  const act_2857579216270778_emit_rcftwbukr = new actionTypes.EmitAction({
    actionArgs: act_2857579216270778_emit_rcftwbukr_getActionArgs,
  });
  const inst_evh_2857579216270778_close_durgugdll =
    new actionTypes.EventHandlerImpl({
      action: act_2857579216270778_emit_rcftwbukr,
      event: 'close',
      when: inst_evh_2857579216270778_close_durgugdll_getWhen,
      displayName: 'emit',
    });
  const act_2857579216270778_showModal_734dyz8qe =
    new actionTypes.ShowModalAction({
      actionArgs: act_2857579216270778_showModal_734dyz8qe_getActionArgs,
      events: [inst_evh_2857579216270778_close_durgugdll],
    });
  const inst_evh_2857579216270778_clickStopPrevent_n26ie598j =
    new actionTypes.EventHandlerImpl({
      action: act_2857579216270778_showModal_734dyz8qe,
      event: 'click.stop.prevent',
      displayName: 'showModal',
    });
  const inst_evh_2857579216270778_clickStopPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2857579216270778_clickStopPrevent_n26ie598j],
      event: 'click.stop.prevent',
    });

  const actions = {
    inst_evh_7315092382398562_reload_b3xojrblp,
    inst_evh_7315092382398562_reload,
    inst_evh_8169383091859921_close_1b33h1f9o,
    inst_evh_8169383091859921_clickStopPrevent_gqo7lnpl5,
    inst_evh_8169383091859921_clickStopPrevent,
    inst_evh_2857579216270778_close_durgugdll,
    inst_evh_2857579216270778_clickStopPrevent_n26ie598j,
    inst_evh_2857579216270778_clickStopPrevent,
  };

  const evh_7315092382398562_reload_b3xojrblp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_b3xojrblp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8169383091859921_close_1b33h1f9o = async (event, scope) => {
    return await actions.inst_evh_8169383091859921_close_1b33h1f9o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8169383091859921_clickStopPrevent_gqo7lnpl5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8169383091859921_clickStopPrevent_gqo7lnpl5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8169383091859921_clickStopPrevent = async (event, scope) => {
    return await actions.inst_evh_8169383091859921_clickStopPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2857579216270778_close_durgugdll = async (event, scope) => {
    return await actions.inst_evh_2857579216270778_close_durgugdll.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2857579216270778_clickStopPrevent_n26ie598j = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2857579216270778_clickStopPrevent_n26ie598j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2857579216270778_clickStopPrevent = async (event, scope) => {
    return await actions.inst_evh_2857579216270778_clickStopPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox spacing="pe-3 py-1" alignItems="center" v-if="loaded">
      <UcContractStatusBadge :value="contract.status"> </UcContractStatusBadge>
      <ItpBox class="ms-auto">
        <template v-for="state in states">
          <ItpButton
            :key="state.value"
            size="sm"
            :variant="state.variant"
            :text="state.text"
            class="mx-2 border"
            v-if="canTransitionTo(state.value, contract.metadata)"
            @click.stop.prevent="
              evh_8169383091859921_clickStopPrevent($event, { state })
            "
          >
          </ItpButton>
        </template>
        <ItpButton
          size="sm"
          variant="light"
          :text="pgettext('SetContractStatus', 'Unarchive...')"
          class="mx-2 border"
          v-if="contract.status === 'archived'"
          @click.stop.prevent="
            evh_2857579216270778_clickStopPrevent($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </ItpBox>
  </ItpUsercontrol>
</template>
